export type FishLogEvent = {
	eventType: string
	start: Date | null
	end: Date | null
	vesselName: string
}

export type NormalizedFishLogEvent = {
	label: string
	key: string
	startMs: number
	endMs: number
}

export type FishLogIntervalSummary = {
	labels: string[]
	count: number
	totalOverlapMs: number
	primaryLabel: string
}

export const FISH_LOG_LOOKBACK_DAYS_DEFAULT = 7

const DEFAULT_FISH_MODE_PALETTE = [
	"rgba(239, 68, 68, 0.70)",
	"rgba(34, 197, 94, 0.70)",
	"rgba(59, 130, 246, 0.70)",
	"rgba(245, 158, 11, 0.70)",
	"rgba(168, 85, 247, 0.70)",
	"rgba(236, 72, 153, 0.70)",
]

export function getFishLogQueryStartWithLookback(
	start: Date | undefined,
	lookbackDays = FISH_LOG_LOOKBACK_DAYS_DEFAULT
): Date | undefined {
	if (start === undefined) {
		return undefined
	}
	const lookbackStart = new Date(start)
	lookbackStart.setDate(lookbackStart.getDate() - lookbackDays)
	return lookbackStart
}

export function normalizeFishModeKey(eventTypeRaw: string | null | undefined): string {
	return `fish-${(eventTypeRaw ?? "").trim().toLowerCase().replace(/\s+/g, "-")}`
}

export function normalizeFishLogEvents(events: FishLogEvent[]): NormalizedFishLogEvent[] {
	return events
		.filter((fishEvent: FishLogEvent) => fishEvent.start !== null)
		.map((fishEvent: FishLogEvent) => {
			const label = fishEvent.eventType.trim()
			return {
				label,
				key: normalizeFishModeKey(label),
				startMs: new Date(fishEvent.start as Date).getTime(),
				endMs:
					fishEvent.end !== null
						? new Date(fishEvent.end).getTime()
						: Number.POSITIVE_INFINITY,
			}
		})
}

export function getFishModeLabelForInterval(
	events: NormalizedFishLogEvent[],
	start: Date | string,
	end: Date | string
): string {
	const summary = summarizeFishLogInterval(events, start, end)
	return summary.labels.length > 0 ? summary.labels.join(", ") : "--"
}

export function summarizeFishLogInterval(
	events: NormalizedFishLogEvent[],
	start: Date | string,
	end: Date | string
): FishLogIntervalSummary {
	const startMs = new Date(start).getTime()
	const endMs = new Date(end).getTime()
	if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs >= endMs) {
		return {
			labels: [],
			count: 0,
			totalOverlapMs: 0,
			primaryLabel: "--",
		}
	}

	const labels = new Set<string>()
	const overlapByLabel = new Map<string, number>()
	let count = 0

	for (const event of events) {
		const overlapStart = Math.max(startMs, event.startMs)
		const overlapEnd = Math.min(endMs, event.endMs)
		if (overlapStart >= overlapEnd) {
			continue
		}

		count += 1
		labels.add(event.label)
		overlapByLabel.set(
			event.label,
			(overlapByLabel.get(event.label) ?? 0) + (overlapEnd - overlapStart)
		)
	}

	const primaryLabel =
		[...overlapByLabel.entries()].sort((a, b) => {
			if (b[1] !== a[1]) {
				return b[1] - a[1]
			}
			return a[0].localeCompare(b[0])
		})[0]?.[0] ?? "--"

	const labelArray = [...labels]
	const orderedLabels =
		primaryLabel !== "--"
			? [
					primaryLabel,
					...labelArray.filter((l) => l !== primaryLabel).sort((a, b) => a.localeCompare(b)),
				]
			: labelArray.sort((a, b) => a.localeCompare(b))

	return {
		labels: orderedLabels,
		count,
		totalOverlapMs: [...overlapByLabel.values()].reduce((sum, value) => sum + value, 0),
		primaryLabel,
	}
}

export function getFishModesAtTimestamp(
	events: NormalizedFishLogEvent[],
	timestampMs: number
): string[] {
	if (Number.isNaN(timestampMs)) {
		return []
	}
	return [
		...new Set(
			events
				.filter(
					(fishEvent) => fishEvent.startMs <= timestampMs && timestampMs < fishEvent.endMs
				)
				.map((fishEvent) => fishEvent.label)
		),
	]
}

export function buildFishModeBgRanges(
	events: NormalizedFishLogEvent[],
	from: Date | string | undefined,
	to: Date | string | undefined
): [number, number, string][] {
	if (from === undefined || to === undefined) {
		return []
	}
	const fromMs = new Date(from).getTime()
	const toMs = new Date(to).getTime()
	if (Number.isNaN(fromMs) || Number.isNaN(toMs) || fromMs >= toMs) {
		return []
	}

	const ranges: [number, number, string][] = []
	for (const fishEvent of events) {
		const overlapStart = Math.max(fromMs, fishEvent.startMs)
		const overlapEnd = Math.min(toMs, fishEvent.endMs)
		if (overlapStart < overlapEnd) {
			ranges.push([
				new Date(overlapStart).setUTCSeconds(0, 0),
				new Date(overlapEnd).setUTCSeconds(0, 0),
				fishEvent.key,
			])
		}
	}
	return ranges
}

export function buildFishModeColorMap(
	events: NormalizedFishLogEvent[],
	palette = DEFAULT_FISH_MODE_PALETTE
): Record<string, string> {
	if (palette.length == 0) {
		return {}
	}
	const uniqueByKey = new Map<string, string>()
	for (const event of events) {
		if (event.label.length === 0) {
			continue
		}
		uniqueByKey.set(event.key, event.label)
	}
	const sortedEntries = [...uniqueByKey.entries()].sort((a, b) => a[1].localeCompare(b[1]))
	const paletteLen = palette.length
	return Object.fromEntries(
		sortedEntries.map(([key], index): [string, string] => [key, palette[index % paletteLen]!])
	)
}

export function buildFishModeLegendItems(
	events: NormalizedFishLogEvent[],
	colorMap: Record<string, string>,
	activeKeys: Set<string>
): { label: string; color: string }[] {
	const entriesByKey = new Map<string, { label: string; color: string }>()
	for (const fishEvent of events) {
		if (fishEvent.label.length === 0 || activeKeys.has(fishEvent.key) === false) {
			continue
		}
		entriesByKey.set(fishEvent.key, {
			label: fishEvent.label,
			color: colorMap[fishEvent.key] ?? "rgba(0, 0, 0, 0.5)",
		})
	}
	return [...entriesByKey.values()].sort((a, b) => a.label.localeCompare(b.label))
}
